import { useState, useEffect } from 'react';
import { useAuthStore } from '@/shared/stores/authStore';
import {
    User,
    CreditCard,
    Shield,
    Save,
    Building2,
    DollarSign,
    Wallet,
    LogOut
} from 'lucide-react';
import Header from '@/react-app/components/Header';
import { useTheme } from '@/react-app/contexts/ThemeContext';
import { useNavigate } from "react-router-dom";
import type { VaultData } from '@/shared/types';
import { useAppKit, useAppKitAccount, useDisconnect } from '@reown/appkit/react';
import { useWalletClient, usePublicClient } from 'wagmi';
import { erc20Abi, formatUnits, parseUnits } from 'viem';
import {
    contractData,
    vaultAddress,
    usdtAddress
} from '@/react-app/components/web3';
import Swal from 'sweetalert2';
import { notifications } from '@mantine/notifications';

export default function Vault() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { user: authStoreUser, isAuthenticated } = useAuthStore();
    const { open } = useAppKit();
    const { disconnect } = useDisconnect();
    const { isConnected, address } = useAppKitAccount();
    const { data: walletClient } = useWalletClient();
    const publicClient = usePublicClient();
    const [txnCount, setTnxCount] = useState(0);
    const [balance, setBalance] = useState('');



    const [vaultData, setVaultData] = useState<VaultData>({
        receiver: '',
        amount: '',
        txnIndex: 0
    });

    useEffect(() => {
        setTimeout(() => setLoading(false), 1500);
    }, []);

    useEffect(() => {
        readVault()
    }, [address, isConnected])

    const readVault = async () => {
        const { vaultAbi } = await contractData();
        const tnx_counts: any = await publicClient?.readContract({
            address: vaultAddress as `0x${string}`,
            abi: vaultAbi,
            functionName: 'getTransactionCount'
        });

        setTnxCount(tnx_counts);

        const rawBalance = await publicClient?.readContract({
            address: usdtAddress,
            abi: erc20Abi,
            functionName: 'balanceOf',
            args: [vaultAddress],
        });

        const formattedBalance = parseFloat(formatUnits(rawBalance as bigint, 18)).toFixed(2);
        setBalance(formattedBalance);
    }

    const tabs = [
        {
            id: 0,
            label: 'Submit Transaction',
            icon: User,
            description: 'Fill in details to submit transaction'
        },
        {
            id: 1,
            label: 'Confirm Transaction',
            icon: CreditCard,
            description: 'Enter transaction index to confirm'
        },
        {
            id: 2,
            label: 'Execute Transaction',
            icon: Shield,
            description: 'Enter transaction index to execute transaction'
        }
    ];




    const handleInputChange = (field: keyof VaultData, value: string) => {
        setVaultData(prev => ({ ...prev, [field]: value }));
    };


    const handleSave = async (tabIndex: number) => {
        setSaving(true);

        try {
            const formData = new FormData();

            if (!isConnected || !walletClient) {
                Swal.fire({
                    text: 'Wallet not connected or provider not available',
                    icon: 'error',
                    confirmButtonColor: '#ff7849',
                })
                return;
            }

            if (!address || !publicClient) return;
            const { vaultAbi } = await contractData();

            if (tabIndex === 0) {
                formData.append('receiver', vaultData.receiver || '');
                formData.append('amount', vaultData.amount || '');
                formData.append('tabIndex', '0');



                if (Number(vaultData.amount) > Number(balance)) {

                    Swal.fire({
                        text: `USDT balance of Vault is: ${balance}`,
                        icon: 'error',
                        confirmButtonColor: '#ff7849',
                    })
                    return;
                }


                const investHash = await walletClient.writeContract({
                    address: vaultAddress as `0x${string}`,
                    abi: vaultAbi,
                    functionName: 'submitTransaction',
                    args: [
                        vaultData.receiver,
                        parseUnits(vaultData.amount.toString(), 18),
                        usdtAddress
                    ],
                });

                const investReceipt = await publicClient?.waitForTransactionReceipt({
                    hash: investHash,
                });

                if (investReceipt?.status === 'success') {
                    notifications.show({
                        message: 'Withdrawal Request submitted!',
                    });
                    return true;
                } else {
                    notifications.show({
                        message: 'Withdrawal request failed',
                    });
                    return false;
                }

            } else if (tabIndex === 1) {

                const investHash = await walletClient.writeContract({
                    address: vaultAddress as `0x${string}`,
                    abi: vaultAbi,
                    functionName: 'confirmTransaction',
                    args: [
                        vaultData.txnIndex
                    ],
                });

                const investReceipt = await publicClient?.waitForTransactionReceipt({
                    hash: investHash,
                });

                if (investReceipt?.status === 'success') {
                    notifications.show({
                        message: 'Confirmation successful',
                    });
                    return true;
                } else {
                    notifications.show({
                        message: 'Confirmation transaction failed',
                    });
                    return false;
                }


            } else if (tabIndex === 2) {
                const investHash = await walletClient.writeContract({
                    address: vaultAddress as `0x${string}`,
                    abi: vaultAbi,
                    functionName: 'executeTransaction',
                    args: [
                        vaultData.txnIndex
                    ],
                });

                const investReceipt = await publicClient?.waitForTransactionReceipt({
                    hash: investHash,
                });

                if (investReceipt?.status === 'success') {
                    notifications.show({
                        message: 'Withdrawal successful',
                    });
                    return true;
                } else {
                    notifications.show({
                        message: 'Withdrawal transaction failed',
                    });
                    return false;
                }
            }

            setSaving(false);


        } catch (error: any) {
            console.error('withdrawal error:', error);
            notifications.show({
                message: error,
            });
            setSaving(false);
        } finally {
            setSaving(false);
        }
    };


    if (loading) {
        return (
            <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <Header variant="dashboard" />
                <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Loading vault...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated || authStoreUser?.role == "user") {
        navigate('/login');
        return null;
    }

    return (
        <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <Header variant="dashboard" />

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                        Vault Management
                    </h1>
                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        Vault balance(USDT): {balance}<br />
                        Current Transaction Index: {txnCount - 1}

                    </p>


                </div>
                <div className='w-fit mx-auto ml-5 animate-in fade-in duration-300'>
                    {
                        !isConnected ? (
                            <div className="flex flex-col items-center gap-2">
                                <button
                                    onClick={() => open()}
                                    className="flex items-center gap-2 py-2 px-6 bg-blue-600 text-white rounded-full text-sm font-bold hover:bg-blue-700 transition-all active:scale-95"
                                >
                                    <Wallet size={16} />
                                    Connect Wallet
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">

                                <div className="text-sm font-mono text-gray-200 bg-gray-900/50 backdrop-blur-md px-4 py-2.5 rounded-xl border border-gray-700/50 shadow-inner">
                                    <span className="text-green-500 mr-2">●</span>
                                    {address?.slice(0, 3)}...{address?.slice(-4)}
                                </div>


                                <button
                                    onClick={() => disconnect()}
                                    title="Disconnect Wallet"
                                    className="p-2.5 bg-red-500/10 text-red-500 rounded-xl border border-red-500/20 hover:bg-red-500 hover:text-white transition-all duration-300 group"
                                >
                                    <LogOut size={18} className="group-hover:translate-x-0.5 transition-transform" />
                                </button>
                            </div>
                        )
                    }
                </div>


                {/* Tabs */}
                <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-3xl shadow-xl overflow-hidden`}>
                    {/* Tab Navigation */}
                    <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <nav className="flex space-x-8 px-8 pt-6 overflow-x-auto no-scrollbar scroll-smooth">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center space-x-2 pb-4 border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                        }`}
                                >
                                    <tab.icon className="h-5 w-5 flex-shrink-0" />
                                    <span className="font-medium">{tab.label}</span>
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Tab Content */}
                    <div className="p-8">
                        {/* Tab 0: Personal Information */}
                        {activeTab === 0 && (
                            <div className="space-y-8">
                                <div>
                                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                                        Withdrawal Info
                                    </h3>
                                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                                        Fill in details to submit transaction
                                    </p>
                                </div>


                                {/* Personal Details Form */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            Recipient Address
                                        </label>
                                        <div className="relative">
                                            <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                            <input
                                                type="text"
                                                value={vaultData.receiver}
                                                onChange={(e) => handleInputChange('receiver', e.target.value)}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Enter address"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            Amount
                                        </label>
                                        <div className="relative">
                                            <DollarSign className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                            <input
                                                type="number"
                                                value={vaultData.amount}
                                                onChange={(e) => handleInputChange('amount', e.target.value)}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Enter amount"
                                            />
                                        </div>
                                    </div>

                                </div>

                                <div className="flex justify-end">
                                    <button
                                        onClick={() => handleSave(0)}
                                        disabled={saving}
                                        className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                    >
                                        <Save className="h-5 w-5" />
                                        <span>{saving ? 'Processing...' : 'Submit'}</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Tab 1: Payment Details */}
                        {activeTab === 1 && (
                            <div className="space-y-8">
                                <div>
                                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                                        Confirm Transaction
                                    </h3>
                                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                                        Enter Transaction Index to confirm
                                    </p>
                                </div>

                                {/* Bank Account Details */}
                                <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-2xl p-6`}>
                                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center`}>
                                        <Building2 className="h-5 w-5 mr-2 text-blue-600" />
                                        Transaction Index
                                    </h4>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                Index NUmber
                                            </label>
                                            <input
                                                type="text"
                                                value={vaultData.txnIndex}
                                                onChange={(e) => handleInputChange('txnIndex', e.target.value)}
                                                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Enter txn index"
                                            />
                                        </div>


                                    </div>
                                </div>


                                <div className="flex justify-end">
                                    <button
                                        onClick={() => handleSave(1)}
                                        disabled={saving}
                                        className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                    >
                                        <Save className="h-5 w-5" />
                                        <span>{saving ? 'Processing...' : 'Submit'}</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Tab 2: Security */}
                        {activeTab === 2 && (
                            <div className="space-y-8">
                                <div>
                                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                                        Execute Transaction
                                    </h3>
                                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                                        Enter Transaction Index to Execute
                                    </p>
                                </div>

                                {/* Password Update Form */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            Index NUmber
                                        </label>
                                        <input
                                            type="text"
                                            value={vaultData.txnIndex}
                                            onChange={(e) => handleInputChange('txnIndex', e.target.value)}
                                            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
                                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                }`}
                                            placeholder="Enter txn index"
                                        />
                                    </div>


                                </div>

                                <div className="flex justify-end">
                                    <button
                                        onClick={() => handleSave(2)}
                                        disabled={saving}
                                        className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                    >
                                        <Save className="h-5 w-5" />
                                        <span>{saving ? 'Processing...' : 'Submit'}</span>

                                    </button>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </main>
        </div>
    );
}
